import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Input from './Input';
import Button from './Button';
import { SolicitacoesAcessoServiceAPI } from '../../services/solicitacoesAcessoServiceAPI';
import FiliaisService from '../../services/filiaisServiceAPI';
import { mascararCNPJ } from '../../utils/formatters';

interface SolicitacaoAcessoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SolicitacaoAcessoModal: React.FC<SolicitacaoAcessoModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    nomeFornecedor: '',
    email: '',
    telefone: '',
    usuarioDesejado: '',
    senha: '',
    confirmarSenha: '',
    regiao: '',
    cnpj: '',
    filiaisSelecionadas: [] as string[],
    motivo: ''
  });

  const [questionario, setQuestionario] = useState<{
    ja_e_fornecedor: boolean | null;
    realizou_venda: boolean | null;
    tem_carga_agendar: boolean | null;
    realizou_visita_presencial: boolean | null;
    deseja_visita: boolean | null;
    negocio_fechado: boolean | null;
    meio_comunicacao: string;
    observacoes_questionario: string;
  }>({
    ja_e_fornecedor: null,
    realizou_venda: null,
    tem_carga_agendar: null,
    realizou_visita_presencial: null,
    deseja_visita: null,
    negocio_fechado: null,
    meio_comunicacao: '',
    observacoes_questionario: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [erros, setErros] = useState<string[]>([]);
  const [aceitePrivacidade, setAceitePrivacidade] = useState(false);

  const filiais = [
    { codigo: 'FILIAL_1', nome: 'Filial 1 - UP Distribuidora de Produtos e Descartáveis LTDA' },
    { codigo: 'FILIAL_2', nome: 'Filial 2 - Aleplast Comércio de Produtos e Descartáveis LTDA' }
  ];

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFilialToggle = (codigo: string) => {
    setFormData(prev => ({
      ...prev,
      filiaisSelecionadas: prev.filiaisSelecionadas.includes(codigo)
        ? prev.filiaisSelecionadas.filter(f => f !== codigo)
        : [...prev.filiaisSelecionadas, codigo]
    }));
  };

  // Validar força da senha
  const validarSenhaForte = (senha: string) => {
    const requisitos = {
      tamanho: senha.length >= 8,
      maiuscula: /[A-Z]/.test(senha),
      minuscula: /[a-z]/.test(senha),
      numero: /[0-9]/.test(senha),
      especial: /[!@#$%^&*(),.?":{}|<>]/.test(senha)
    };
    return requisitos;
  };

  const requisitosSenha = validarSenhaForte(formData.senha);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErros([]);
    
    // Validações locais primeiro
    const errosLocais: string[] = [];

    if (!formData.nomeFornecedor.trim()) {
      errosLocais.push('Nome do fornecedor é obrigatório');
    }
    if (!formData.email.trim()) {
      errosLocais.push('Email é obrigatório');
    }
    if (!formData.usuarioDesejado.trim()) {
      errosLocais.push('Usuário desejado é obrigatório');
    }
    if (!formData.senha || formData.senha.length < 8) {
      errosLocais.push('Senha deve ter no mínimo 8 caracteres');
    }
    if (formData.cnpj.replace(/\D/g, '').length !== 14) {
      errosLocais.push('CNPJ deve ter 14 dígitos');
    }
    if (!requisitosSenha.maiuscula) {
      errosLocais.push('Senha deve conter pelo menos uma letra maiúscula');
    }
    if (!requisitosSenha.minuscula) {
      errosLocais.push('Senha deve conter pelo menos uma letra minúscula');
    }
    if (!requisitosSenha.numero) {
      errosLocais.push('Senha deve conter pelo menos um número');
    }
    if (!requisitosSenha.especial) {
      errosLocais.push('Senha deve conter pelo menos um caractere especial');
    }
    if (formData.senha !== formData.confirmarSenha) {
      errosLocais.push('As senhas não conferem');
    }
    if (formData.filiaisSelecionadas.length === 0) {
      errosLocais.push('Selecione pelo menos uma filial');
    }
    if (questionario.ja_e_fornecedor === null) {
      errosLocais.push('Responda: Você já é nosso fornecedor?');
    }
    if (questionario.realizou_venda === null) {
      errosLocais.push('Responda: Conseguiu realizar alguma venda para o grupo Aleplast?');
    }
    if (questionario.tem_carga_agendar === null) {
      errosLocais.push('Responda: Tem alguma carga para agendar para a Logística?');
    }
    if (questionario.realizou_visita_presencial === null) {
      errosLocais.push('Responda: Já realizou alguma visita presencial aos nossos compradores?');
    }
    if (questionario.deseja_visita === null) {
      errosLocais.push('Responda: Deseja realizar visita?');
    }
    if (questionario.negocio_fechado === null) {
      errosLocais.push('Responda: O negócio foi fechado?');
    }
    if (!questionario.meio_comunicacao) {
      errosLocais.push('Selecione o meio de comunicação');
    }
    if (questionario.meio_comunicacao === 'Outros' && !questionario.observacoes_questionario?.trim()) {
      errosLocais.push('Especifique o meio de comunicação ao selecionar "Outros"');
    }
    if (!aceitePrivacidade) {
      errosLocais.push('Você deve aceitar a Política de Privacidade');
    }
    if (errosLocais.length > 0) {
      setErros(errosLocais);
      setLoading(false);
      return;
    }
    
    try {
      // Enviar solicitação via API intermediária (validação é feita no backend)
      try {
        const response = await SolicitacoesAcessoServiceAPI.criar({
          nome: formData.nomeFornecedor,
          email: formData.email,
          login: formData.usuarioDesejado,
          senha: formData.senha,
          telefone: formData.telefone,
          perfil_id: 'fornecedor',
          filiais_permitidas: formData.filiaisSelecionadas,
          cnpj: formData.cnpj,
          eh_transportadora: false,
          regiao: formData.regiao,
          filial_pretendida: formData.filiaisSelecionadas.join(', '),
          motivo: formData.motivo,
          ja_e_fornecedor: questionario.ja_e_fornecedor ?? false,
          realizou_venda: questionario.realizou_venda ?? false,
          tem_carga_agendar: questionario.tem_carga_agendar ?? false,
          realizou_visita_presencial: questionario.realizou_visita_presencial ?? false,
          deseja_visita: questionario.deseja_visita ?? false,
          negocio_fechado: questionario.negocio_fechado ?? false,
          meio_comunicacao: questionario.meio_comunicacao,
          observacoes_questionario: questionario.observacoes_questionario
        } as any);

        if (response.success) {
          alert('Solicitação de acesso enviada com sucesso! Você receberá um email quando for aprovada.');
          onClose();
        } else {
          alert('Erro ao enviar solicitação: ' + (response.message || 'Erro desconhecido'));
        }
      } catch (error: any) {
        console.error('Erro ao criar solicitação:', error);
        console.error('Error response:', error.response);
        console.error('Error response data:', error.response?.data);
        
        // Extrair mensagem de erro de várias possíveis fontes
        let mensagensErro: string[] = [];
        
        const data = error.response?.data;
        if (data) {
          // Erro principal
          if (data.error) {
            mensagensErro.push(data.error);
          }
          // Detalhes da validação
          if (data.details && Array.isArray(data.details)) {
            data.details.forEach((d: any) => {
              if (d.msg) mensagensErro.push(`${d.path || 'Campo'}: ${d.msg}`);
              else if (typeof d === 'string') mensagensErro.push(d);
            });
          }
        }
        
        // Se não conseguiu extrair, usa mensagem genérica
        if (mensagensErro.length === 0) {
          mensagensErro.push(error.message || 'Erro ao enviar solicitação. Tente novamente.');
        }
        
        setErros(mensagensErro);
      }
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      setErros(['Erro inesperado ao enviar solicitação. Tente novamente mais tarde.']);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Solicitação Enviada">
        <div className="text-center py-8">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100 mb-4">
            <svg className="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Solicitação realizada com sucesso!</h3>
          <p className="text-gray-500 mb-6">Sua solicitação foi enviada para análise administrativa. Você receberá um e-mail assim que seu acesso for liberado.</p>
          <Button onClick={onClose} className="w-full">Fechar</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Solicitar Cadastro de Login">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-500 mb-4">
          Preencha os dados abaixo para solicitar seu acesso ao sistema. Todos os campos com * são obrigatórios.
        </p>

        {erros.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-red-500 mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-red-800 mb-1">Por favor, corrija os seguintes erros:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  {erros.map((erro, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-1">•</span>
                      <span>{erro}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}


        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Input
              label="Nome do Fornecedor *"
              placeholder="Razão social ou nome do fornecedor"
              value={formData.nomeFornecedor}
              onChange={(val) => handleChange('nomeFornecedor', val)}
              required
            />
          </div>

          <Input
            label="E-mail *"
            type="email"
            placeholder="email@fornecedor.com"
            value={formData.email}
            onChange={(val) => handleChange('email', val)}
            required
          />

          <Input
            label="Telefone para contato"
            placeholder="(00) 00000-0000"
            value={formData.telefone}
            onChange={(val) => handleChange('telefone', val)}
          />

          <Input
            label="Usuário desejado"
            placeholder="Login desejado"
            value={formData.usuarioDesejado}
            onChange={(val) => handleChange('usuarioDesejado', val)}
          />

          <Input
            label="CNPJ *"
            placeholder="00.000.000/0000-00"
            value={formData.cnpj}
            onChange={(val) => handleChange('cnpj', mascararCNPJ(val))}
            required
          />

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filiais para as quais pretende agendar cargas *
            </label>
            <div className="space-y-2">
              {filiais.map(f => (
                <label key={f.codigo} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    checked={formData.filiaisSelecionadas.includes(f.codigo)}
                    onChange={() => handleFilialToggle(f.codigo)}
                  />
                  <span className="text-sm text-gray-700">{f.nome}</span>
                </label>
              ))}
            </div>
          </div>

          <Input
            label="Senha desejada *"
            type="password"
            placeholder="Mínimo 8 caracteres"
            value={formData.senha}
            onChange={(val) => handleChange('senha', val)}
            required
          />

          {/* Indicador visual de força da senha */}
          {formData.senha && (
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mt-2">
              <p className="text-xs font-medium text-gray-700 mb-2">Requisitos da senha:</p>
              <ul className="space-y-1 text-xs">
                <li className={requisitosSenha.tamanho ? 'text-green-600' : 'text-red-600'}>
                  {requisitosSenha.tamanho ? '✅' : '❌'} Mínimo 8 caracteres
                </li>
                <li className={requisitosSenha.maiuscula ? 'text-green-600' : 'text-red-600'}>
                  {requisitosSenha.maiuscula ? '✅' : '❌'} Pelo menos uma letra maiúscula
                </li>
                <li className={requisitosSenha.minuscula ? 'text-green-600' : 'text-red-600'}>
                  {requisitosSenha.minuscula ? '✅' : '❌'} Pelo menos uma letra minúscula
                </li>
                <li className={requisitosSenha.numero ? 'text-green-600' : 'text-red-600'}>
                  {requisitosSenha.numero ? '✅' : '❌'} Pelo menos um número
                </li>
                <li className={requisitosSenha.especial ? 'text-green-600' : 'text-red-600'}>
                  {requisitosSenha.especial ? '✅' : '❌'} Pelo menos um caractere especial (!@#$%&*)
                </li>
              </ul>
            </div>
          )}

          <Input
            label="Confirmar senha *"
            type="password"
            placeholder="Repita a senha"
            value={formData.confirmarSenha}
            onChange={(val) => handleChange('confirmarSenha', val)}
            required
          />

          <div className="md:col-span-2">
            <Input
              label="Região que o fornecedor reside"
              placeholder="Cidade / Estado / Região"
              value={formData.regiao}
              onChange={(val) => handleChange('regiao', val)}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo da solicitação (opcional)
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
              placeholder="Descreva o motivo da sua solicitação de acesso ao sistema (opcional)..."
              value={formData.motivo}
              onChange={(e) => handleChange('motivo', e.target.value)}
            />
          </div>
        </div>

        <div className="md:col-span-2 mt-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-3">📋 Questionário de Identificação</h4>
            <div className="space-y-3">
              {[
                { key: 'ja_e_fornecedor', label: 'Você já é nosso fornecedor?' },
                { key: 'realizou_venda', label: 'Conseguiu realizar alguma venda para o grupo Aleplast?' },
                { key: 'tem_carga_agendar', label: 'Tem alguma carga para agendar para a Logística?' },
                { key: 'realizou_visita_presencial', label: 'Já realizou alguma visita presencial aos nossos compradores?' },
                { key: 'deseja_visita', label: 'Deseja realizar visita?' },
                { key: 'negocio_fechado', label: 'O negócio foi fechado?' }
              ].map((p) => (
                <div key={p.key}>
                  <label className="block text-sm text-gray-700 mb-1">{p.label}</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name={p.key}
                        checked={(questionario as any)[p.key] === true}
                        onChange={() => setQuestionario(prev => ({ ...prev, [p.key]: true }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Sim</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name={p.key}
                        checked={(questionario as any)[p.key] === false}
                        onChange={() => setQuestionario(prev => ({ ...prev, [p.key]: false }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Não</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">Qual meio de comunicação você entrou em contato conosco?</label>
              <div className="space-y-1.5">
                {['WhatsApp', 'Telefone', 'Email', 'Falcon Hub Agendamentos'].map(opt => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="meio_comunicacao"
                      checked={questionario.meio_comunicacao === opt}
                      onChange={() => setQuestionario(prev => ({ ...prev, meio_comunicacao: opt }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{opt}</span>
                  </label>
                ))}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="meio_comunicacao"
                    checked={questionario.meio_comunicacao === 'Outros'}
                    onChange={() => setQuestionario(prev => ({ ...prev, meio_comunicacao: 'Outros' }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Outros</span>
                </label>
                {questionario.meio_comunicacao === 'Outros' && (
                  <input
                    type="text"
                    value={questionario.observacoes_questionario || ''}
                    onChange={(e) => setQuestionario(prev => ({ ...prev, observacoes_questionario: e.target.value }))}
                    placeholder="Especifique o meio de comunicação..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mt-1"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-3 rounded-lg text-[11px] text-gray-500 border border-gray-200 mt-4">
          Dica: Use pelo menos 8 caracteres com letras maiúsculas, minúsculas, números e símbolos (!@#$%&*)
        </div>

        <div className="mt-6">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={aceitePrivacidade}
              onChange={(e) => setAceitePrivacidade(e.target.checked)}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
              required
            />
            <span className="text-sm text-gray-700">
              Li e aceito a{' '}
              <a
                href="/politica-privacidade"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline font-medium"
              >
                Política de Privacidade
              </a>
              {' '}e concordo com o tratamento dos meus dados pessoais conforme a LGPD.
            </span>
          </label>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            type="submit"
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            loading={loading}
          >
            📤 Enviar Solicitação
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
          >
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SolicitacaoAcessoModal;
